import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
// Fix: Use the exported instances from our firebase config which now uses compat API to resolve member errors
import { auth, db, googleProvider } from '../firebase';

// 1. User Profile: Identity & Subscription Status
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  phone?: string;
  businessName?: string;

  // Legacy / Single-App fallback
  subscription: {
    planId: 'explorer' | 'starter' | 'growth' | 'creator' | 'enterprise';
    status: 'active' | 'inactive' | 'past_due' | 'canceled';
    nextBillingDate?: string;
    stripeCustomerId?: string;
  };

  // [CRITICAL] Unified Platform Subscriptions Map
  // Read-Only on Hub, Writable on Spokes
  subscriptions?: {
    [appId: string]: {
      planId: 'explorer' | 'starter' | 'growth' | 'creator' | 'enterprise';
      status: 'active' | 'inactive' | 'past_due' | 'canceled';
      nextBillingDate?: string;
    }
  };
}

// 2. User Wallet: The Currency (Hybrid Model)
export interface UserWallet {
  balance_subscription: number; // Expires monthly
  balance_topup: number;        // Never expires
  total_available: number;      // Sum of both
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  userWallet: UserWallet | null;
  isAdmin: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  launchAppWithToken: (url: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userWallet, setUserWallet] = useState<UserWallet | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Function to fetch User Data (Profile + Wallet)
  const fetchUserData = async (uid: string, user: User) => {
    try {
      // --- 1. Fetch Profile (users/{uid}) ---
      let profileData: UserProfile;
      const userDoc = await db.collection('users').doc(uid).get();

      if (userDoc.exists) {
        const data = userDoc.data();
        profileData = {
          uid,
          email: data?.email || user.email || '',
          displayName: data?.displayName || user.displayName || '',
          photoURL: data?.photoURL || user.photoURL || '',
          phone: data?.phone || '',
          businessName: data?.businessName || '',
          subscription: data?.subscription || {
            planId: 'explorer',
            status: 'active'
          },
          subscriptions: data?.subscriptions || {} // Direct mapping of the multi-app map
        };
      } else {
        // Create default "Explorer" profile
        profileData = {
          uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          subscription: {
            planId: 'explorer',
            status: 'active',
            nextBillingDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString()
          },
          subscriptions: {
            opusgo: { planId: 'explorer', status: 'active' },
            opus3d: { planId: 'explorer', status: 'active' },
            opusstudio: { planId: 'explorer', status: 'active' }
          }
        };
        // Silent write attempt
        try {
          await db.collection('users').doc(uid).set(profileData, { merge: true });
          console.log(`[AuthContext] Created user profile for ${uid}`);
        } catch (e) {
          console.warn("Profile creation blocked", e);
        }
      }
      setUserProfile(profileData);

      // --- 2. Fetch Wallet (wallets/{uid}) ---
      let walletData: UserWallet;
      const walletDoc = await db.collection('wallets').doc(uid).get();

      if (walletDoc.exists) {
        walletData = walletDoc.data() as UserWallet;
        walletData.total_available = (walletData.balance_subscription || 0) + (walletData.balance_topup || 0);
      } else {
        // Initialize Default Wallet based on Plan
        const initialSubCredits = profileData.subscription.planId === 'creator' ? 1000 : 50;
        walletData = {
          balance_subscription: initialSubCredits,
          balance_topup: 0,
          total_available: initialSubCredits
        };
        try {
          await db.collection('wallets').doc(uid).set(walletData);
          console.log(`[AuthContext] ✅ Wallet successfully created in Firestore for user: ${uid}`);
        } catch (e) {
          console.warn(`[AuthContext] ❌ Wallet creation failed for user: ${uid}. Check Firestore Rules.`, e);
        }
      }
      setUserWallet(walletData);

    } catch (error) {
      console.warn("Error initializing user data:", error);
      // Fail-safe defaults
      setUserProfile({
        uid, email: user.email || '', displayName: user.displayName || '',
        subscription: { planId: 'explorer', status: 'active' }
      });
      setUserWallet({ balance_subscription: 50, balance_topup: 0, total_available: 50 });
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user as User | null);

      if (user) {
        // Parallel execution: Admin Check + User Data Fetch
        const adminCheck = db.collection('admins').doc(user.uid).get()
          .then((snap) => setIsAdmin(snap.exists))
          .catch(() => setIsAdmin(false));

        const userDataFetch = fetchUserData(user.uid, user as User);

        await Promise.all([adminCheck, userDataFetch]);
      } else {
        setIsAdmin(false);
        setUserProfile(null);
        setUserWallet(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    await auth.signOut();
    setUserProfile(null);
    setUserWallet(null);
  };

  const signInWithGoogle = async () => {
    await auth.signInWithPopup(googleProvider);
  };

  const resendVerification = async () => {
    if (auth.currentUser) {
      await auth.currentUser.sendEmailVerification();
    }
  };

  const refreshProfile = async () => {
    if (currentUser) {
      await fetchUserData(currentUser.uid, currentUser);
    }
  };

  const launchAppWithToken = async (url: string) => {
    if (!url || url === '#' || url === 'javascript:void(0)') return;

    if (currentUser) {
      try {
        // Generate a fresh ID token (force refresh to ensure validity)
        const token = await currentUser.getIdToken(true);
        const urlObj = new URL(url);
        // Append token as query parameter
        urlObj.searchParams.set('token', token);
        window.open(urlObj.toString(), '_blank');
      } catch (error) {
        console.error("[AuthContext] Error launching app with token:", error);
        // Fallback: open without token if generation fails
        window.open(url, '_blank');
      }
    } else {
      // Not logged in, just open the link
      window.open(url, '_blank');
    }
  };

  const value = {
    currentUser,
    userProfile,
    userWallet,
    isAdmin,
    loading,
    logout,
    signInWithGoogle,
    resendVerification,
    refreshProfile,
    launchAppWithToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};